import React, { useRef, useCallback } from "react";

/**
 * Horizontal hue strip slider.
 *
 * @param {object}   props
 * @param {number}   props.hue      - current hue [0, 360)
 * @param {Function} props.onChange - (hue: number) => void
 */
const HueSlider = ({ hue, onChange }) => {
  const trackRef = useRef(null);
  const isDragging = useRef(false);

  const applyPointer = useCallback(
    (clientX) => {
      const track = trackRef.current;

      if (!track) return;

      const rect = track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));

      onChange(Math.round(ratio * 360));
    },
    [onChange],
  );

  // Pointer events with capture: a finger drags here too, also past the edges
  const handlePointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    isDragging.current = true;
    applyPointer(e.clientX);
  };

  const handlePointerMove = (e) => {
    if (isDragging.current) applyPointer(e.clientX);
  };

  const handlePointerEnd = () => {
    isDragging.current = false;
  };

  const cursorLeft = `${(hue / 360) * 100}%`;

  return (
    <div
      ref={trackRef}
      className="hue-slider"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={360}
      aria-valuenow={hue}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") onChange(Math.max(0, hue - 1));
        if (e.key === "ArrowRight") onChange(Math.min(360, hue + 1));
      }}
    >
      <div
        className="hue-slider__cursor"
        style={{ left: cursorLeft }}
        aria-hidden="true"
      />
    </div>
  );
};

export default HueSlider;
